import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Repeat, FileCheck } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';
import { track, TRIAL_CTA, DEMO_CTA } from '../utils/analytics';

const PROOF_SHOT =
  'w-full h-auto border border-gray-200 shadow-[0_18px_50px_rgba(18,33,64,0.08)]';

export default function Home() {
  usePageMeta(
    'Tactmark | Football & Soccer Analysis Software for Teams and Federations',
    'Football and soccer analysis software for coaching staff. Tactmark Post-Match turns match evidence into coaching decisions; Tactmark Federation gives associations technical development oversight.'
  );
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      {/* Hero — brand-led, one composition */}
      <section className="relative overflow-hidden border-b border-gray-200">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-white"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 right-[-10%] h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(15,31,61,0.06),transparent_68%)]"
        />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-16 sm:pt-24 sm:pb-20">
          <p className="font-serif text-4xl sm:text-5xl md:text-6xl text-navy-900 tracking-tight mb-6">
            Tactmark
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-navy-900 leading-tight max-w-3xl">
            Football and soccer analysis for coaching decisions.
          </h1>
          <p className="text-gray-600 text-lg mt-6 max-w-2xl leading-relaxed">
            Two products with separate responsibilities: Post-Match football and soccer analysis after a match,
            and Federation for technical development across programs.
          </p>
          <div className="flex items-center gap-4 mt-9 flex-wrap">
            <Link
              to="/products"
              className="bg-navy-900 hover:bg-navy-800 text-white font-medium px-6 py-3 rounded transition-colors"
            >
              View products
            </Link>
            <Link
              to="/contact?plan=federation&from=home_hero"
              className="border border-navy-900 text-navy-900 font-medium px-6 py-3 rounded hover:bg-navy-50 transition-colors"
            >
              Book a Federation demo
            </Link>
            <Link
              to="/login?mode=signup"
              onClick={() => track(TRIAL_CTA, { from: 'home_hero' })}
              className="text-navy-900 font-medium hover:underline"
            >
              Start Post-Match trial
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-6 max-w-2xl">
            Looking for{' '}
            <Link to="/football-analysis-software" className="text-navy-900 hover:underline">
              football analysis software
            </Link>
            ,{' '}
            <Link to="/soccer-analysis-software" className="text-navy-900 hover:underline">
              soccer analysis software
            </Link>
            , or{' '}
            <Link to="/insights" className="text-navy-900 hover:underline">
              match analysis insights
            </Link>
            ?
          </p>
        </div>
      </section>

      {/* Two products — Dagmont-style alternating proof */}
      <section className="bg-slate-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
          <h2 className="font-serif text-3xl sm:text-4xl text-navy-900 mb-4">
            Two products. Clear value for each.
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mb-14">
            Post-Match owns the coaching report after a match. Federation owns program oversight,
            submissions, and technical follow-through across the organization.
          </p>

          {/* Post-Match: copy left, shot right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-16 lg:mb-20">
            <div className="lg:col-span-5">
              <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-3">
                For coaches and teams
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-4">
                Tactmark Post-Match
              </h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                Turn key moments, coaching observations, and match evidence into a structured
                post-match report: what decided the match, the decisive risk, and the training
                priority for the next session.
              </p>
              <Link
                to="/products/post-match"
                className="inline-flex text-navy-900 font-medium hover:underline"
              >
                Explore Post-Match
              </Link>
            </div>
            <figure className="lg:col-span-7 m-0">
              <img
                src="/product-proof/post-match-desktop.png"
                alt="Tactmark Post-Match analysis report showing match snapshot, decisive risk, and training priority for Riverside FC vs Valley United"
                className={PROOF_SHOT}
                width={1440}
                height={900}
              />
              <figcaption className="mt-3 text-sm text-gray-500">
                Tactmark Post-Match: match snapshot, decisive risk, and the main training priority from submitted evidence.
              </figcaption>
            </figure>
          </div>

          {/* Authentic football context — restored stadium visual after Post-Match proof */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-16 lg:mb-20">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-3">
                After the final whistle
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-4">
                Where coaching staff turn the match into the next decision.
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Post-Match is built for the work that happens when the stadium lights are still on:
                key moments logged, evidence reviewed, and a coaching priority locked before the next session.
              </p>
            </div>
            <figure className="lg:col-span-7 m-0 order-1 lg:order-2">
              <img
                src="/marketing/post-match-stadium.jpg"
                alt="Coaching staff reviewing a post-match report overlooking a night football stadium"
                className="w-full h-auto object-cover border border-gray-200 shadow-[0_18px_50px_rgba(18,33,64,0.08)] animate-[fadeRise_700ms_ease-out]"
                width={1024}
                height={1024}
                loading="lazy"
              />
              <figcaption className="mt-3 text-sm text-gray-500">
                Match-night context for Post-Match: review evidence while the game is still fresh.
              </figcaption>
            </figure>
          </div>

          {/* Federation: shot left, copy right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center mb-16 lg:mb-20">
            <figure className="lg:col-span-7 m-0 order-2 lg:order-1">
              <img
                src="/product-proof/federation-desktop.png"
                alt="Tactmark Federation findings showing unresolved technical priorities across development pathways"
                className={PROOF_SHOT}
                width={1440}
                height={900}
              />
              <figcaption className="mt-3 text-sm text-gray-500">
                Tactmark Federation: unresolved findings and technical priorities from program submissions across pathways.
              </figcaption>
            </figure>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-3">
                For football associations
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-4">
                Tactmark Federation
              </h3>
              <p className="text-gray-600 leading-relaxed mb-5">
                Set development objectives, collect program submissions, track unresolved findings
                and assigned actions, and keep a durable technical record across youth, senior,
                women’s, academy, and coach-development pathways.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="/products/federation"
                  className="inline-flex text-navy-900 font-medium hover:underline"
                >
                  Explore Federation
                </Link>
                <Link
                  to="/contact?plan=federation&from=home_proof"
                  onClick={() => track(DEMO_CTA, { plan: 'federation', from: 'home_proof' })}
                  className="inline-flex text-navy-700 text-sm font-medium hover:underline"
                >
                  Book a Federation Demo
                </Link>
              </div>
            </div>
          </div>

          {/* Authentic football context for Federation oversight */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <figure className="lg:col-span-7 m-0">
              <img
                src="/marketing/federation-pitch.jpg"
                alt="Technical staff reviewing match analysis from a stadium press box overlooking the pitch"
                className="w-full h-auto object-cover border border-gray-200 shadow-[0_18px_50px_rgba(18,33,64,0.08)] animate-[fadeRise_700ms_ease-out]"
                width={1024}
                height={1024}
                loading="lazy"
              />
              <figcaption className="mt-3 text-sm text-gray-500">
                Federation context: technical oversight across programs, not one team on one night.
              </figcaption>
            </figure>
            <div className="lg:col-span-5">
              <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-3">
                Across the organization
              </p>
              <h3 className="font-serif text-2xl sm:text-3xl text-navy-900 mb-4">
                The same match evidence, read at federation level.
              </h3>
              <p className="text-gray-600 leading-relaxed">
                When pathways submit work and findings stay open, technical leadership can see what
                still needs a coaching response before the next reporting cycle closes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 sm:grid-cols-3 gap-10">
        {[
          {
            icon: AlertTriangle,
            title: 'Evidence over opinion',
            text: 'Whether reviewing a match or a development program, decisions stay grounded in submitted evidence and explicit review status.',
          },
          {
            icon: Repeat,
            title: 'Accountability over time',
            text: 'Recurring Post-Match patterns and Federation actions stay visible across reporting periods instead of disappearing after one meeting.',
          },
          {
            icon: FileCheck,
            title: 'Reports people can act on',
            text: 'Coaching reports and Federation leadership exports are structured for the people who own the next decision.',
          },
        ].map(({ icon: Icon, title, text }) => (
          <div key={title}>
            <div className="w-9 h-9 bg-navy-900 rounded-lg flex items-center justify-center mb-4">
              <Icon className="w-4.5 h-4.5 text-white" />
            </div>
            <h3 className="text-navy-900 font-semibold mb-2">{title}</h3>
            <p className="text-gray-500 text-sm leading-relaxed">{text}</p>
          </div>
        ))}
      </div>

      {/* Trust */}
      <div className="max-w-6xl mx-auto px-6 py-16 border-t border-gray-200">
        <h2 className="font-serif text-3xl text-navy-900 mb-4">Built for controlled coaching data.</h2>
        <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mb-8">
          Tactmark keeps match evidence and reports inside your organization, with role-based and team-scoped access for coaching staff and Federation leadership when provisioned.
        </p>
        <ul className="grid sm:grid-cols-2 gap-4 text-sm text-gray-600 max-w-3xl">
          {[
            'Organization isolation between customers',
            'Owner, Staff, and Viewer authorization roles',
            'Team-scoped access for coaching staff',
            'Secure evidence storage for uploaded footage references',
            'Private report sharing links under your control',
            'Export and deletion paths for organization owners',
          ].map((item) => (
            <li key={item} className="flex gap-2">
              <span className="text-navy-700">-</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA */}
      <div className="max-w-6xl mx-auto px-6 py-20 text-center border-t border-gray-200">
        <h2 className="font-serif text-3xl text-navy-900 mb-4">
          See the product that matches your responsibility.
        </h2>
        <p className="text-gray-500 max-w-lg mx-auto mb-8">
          Start with Post-Match for team-level coaching reports, or book a Federation demo for organization-level technical oversight.
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/login?mode=signup"
            onClick={() => track(TRIAL_CTA, { from: 'home_footer' })}
            className="inline-block bg-navy-900 hover:bg-navy-800 text-white font-medium px-8 py-3 rounded transition-colors"
          >
            Start Post-Match trial
          </Link>
          <Link
            to="/contact?plan=federation&from=home_footer"
            className="inline-block border border-navy-900 text-navy-900 font-medium px-8 py-3 rounded hover:bg-navy-50 transition-colors"
          >
            Book a Federation demo
          </Link>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
